import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import {
  Button,
  Dialog,
  Input,
  Label,
  Select,
  Stack,
  Textarea,
} from '@easymoving/ui';
import {
  CURRENCIES,
  type Condition,
  type Destination,
  type DimensionUnit,
  type Item,
  type ItemDraft,
  type ProgressStatus,
} from '@easymoving/types';
import { useBackend } from '../../lib/backend/context';
import {
  useBoxes,
  useCustomCategories,
  useCustomRooms,
  useMoveProfile,
} from '../../hooks/useUserData';
import {
  categoryOptions,
  conditionOptions,
  destinationOptions,
  roomOptions,
  statusOptions,
  unitOptions,
} from '../../lib/taxonomy/options';
import { PhotoPicker } from './PhotoPicker';
import { FurnitureTypePicker } from '../furniture/FurnitureTypePicker';
import { useUnits } from '../../lib/units/useUnits';
import { useWorkspace } from '../../context/WorkspaceContext';

const ADD_ROOM = '__add_room__';
const ADD_CATEGORY = '__add_category__';
const NO_BOX = '__none__';

function numOrUndef(s: string): number | undefined {
  const n = Number(s);
  return s.trim() !== '' && Number.isFinite(n) ? n : undefined;
}

export interface ItemFormSubmit {
  draft: ItemDraft;
  file: File | null;
  removeExisting: boolean;
}

export function ItemForm({
  initial,
  busy,
  busyLabel,
  submitLabel,
  onSubmit,
}: {
  initial?: Item;
  busy?: boolean;
  busyLabel?: string;
  submitLabel: string;
  onSubmit: (v: ItemFormSubmit) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const backend = useBackend();
  const customRooms = useCustomRooms();
  const customCategories = useCustomCategories();
  const { boxes } = useBoxes();
  const { profile } = useMoveProfile();
  const units = useUnits();
  const { isOwn } = useWorkspace();

  const [name, setName] = useState(initial?.name ?? '');
  const [nickname, setNickname] = useState(initial?.nickname ?? '');
  const [room, setRoom] = useState(initial?.room ?? '');

  // For a room→room/room→home mover, default a NEW item's room to the source
  // room — but only once, and never over an existing room (editing or a pick).
  const seededRoom = useRef(false);
  useEffect(() => {
    if (seededRoom.current) return;
    if (initial?.room || room) return;
    if (profile?.originScope === 'room' && profile.sourceRoom) {
      setRoom(profile.sourceRoom);
      seededRoom.current = true;
    }
  }, [initial?.room, room, profile?.originScope, profile?.sourceRoom]);

  const [category, setCategory] = useState(initial?.category ?? '');
  const [destination, setDestination] = useState<Destination>(initial?.destination ?? 'pack');
  const [status, setStatus] = useState<ProgressStatus>(initial?.progressStatus ?? 'not_started');
  const [file, setFile] = useState<File | null>(null);
  const [removeExisting, setRemoveExisting] = useState(false);
  const [iconType, setIconType] = useState<string | undefined>(initial?.iconType);

  const [showMore, setShowMore] = useState(false);
  const [length, setLength] = useState(initial?.length?.toString() ?? '');
  const [width, setWidth] = useState(initial?.width?.toString() ?? '');
  const [height, setHeight] = useState(initial?.height?.toString() ?? '');
  const [unit, setUnit] = useState<DimensionUnit | ''>(initial?.dimensionUnit ?? units.defaultDimUnit);
  const [weight, setWeight] = useState(
    initial?.weight != null ? String(Math.round(units.weightToDisplay(initial.weight) * 100) / 100) : '',
  );
  const [condition, setCondition] = useState<Condition | ''>(initial?.condition ?? '');
  const [quantity, setQuantity] = useState(initial?.quantity?.toString() ?? '1');
  const [estimatedValue, setEstimatedValue] = useState(initial?.estimatedValue?.toString() ?? '');
  const [currency, setCurrency] = useState<string>(initial?.currency ?? 'ILS');
  const [boxId, setBoxId] = useState(initial?.boxId ?? '');
  const [storageLocation, setStorageLocation] = useState(initial?.storageLocation ?? '');
  const [fragile, setFragile] = useState(initial?.fragile ?? false);
  const [notes, setNotes] = useState(initial?.notes ?? '');

  const [nameError, setNameError] = useState(false);
  const [customMode, setCustomMode] = useState<null | 'room' | 'category'>(null);
  const [customName, setCustomName] = useState('');
  const [customBusy, setCustomBusy] = useState(false);

  async function confirmCustom() {
    if (!customName.trim() || customBusy) return; // guard against double-submit
    setCustomBusy(true);
    const value = customName.trim();
    try {
      if (customMode === 'room') {
        // Dedupe: only create if no custom room with this name already exists.
        if (!customRooms.some((r) => r.name === value)) await backend.createCustomRoom(value);
        setRoom(value);
      } else if (customMode === 'category') {
        if (!customCategories.some((c) => c.name === value)) await backend.createCustomCategory(value);
        setCategory(value);
      }
      // Close only AFTER the field is set, so a save right after picks it up.
      setCustomName('');
      setCustomMode(null);
    } finally {
      setCustomBusy(false);
    }
  }

  function submit() {
    if (!name.trim()) {
      setNameError(true);
      return;
    }
    const weightVal = numOrUndef(weight);
    const draft: ItemDraft = {
      name: name.trim(),
      nickname: nickname.trim() || undefined,
      room: room || undefined,
      category: category || undefined,
      iconType: iconType || undefined,
      destination,
      progressStatus: status,
      length: numOrUndef(length),
      width: numOrUndef(width),
      height: numOrUndef(height),
      dimensionUnit: unit || undefined,
      weight: weightVal != null ? units.weightFromInput(weightVal) : undefined,
      condition: condition || undefined,
      quantity: numOrUndef(quantity) ?? 1,
      estimatedValue: numOrUndef(estimatedValue),
      currency: estimatedValue ? (currency as (typeof CURRENCIES)[number]) : undefined,
      boxId: boxId || undefined,
      storageLocation: storageLocation || undefined,
      fragile,
      notes: notes.trim() || undefined,
    };
    void onSubmit({ draft, file, removeExisting });
  }

  const roomOpts = [
    ...roomOptions(t, customRooms),
    { value: ADD_ROOM, label: `＋ ${t('item.addCustomRoom')}` },
  ];
  const categoryOpts = [
    ...categoryOptions(t, customCategories),
    { value: ADD_CATEGORY, label: `＋ ${t('item.addCustomCategory')}` },
  ];
  const boxOpts = [
    { value: NO_BOX, label: t('item.noBox') },
    ...boxes.map((b) => ({ value: b.id, label: b.name })),
  ];

  return (
    <Stack gap="md">
      {isOwn && (
        <PhotoPicker
          file={file}
          existingUrl={removeExisting ? undefined : initial?.primaryImageUrl}
          onChange={(f) => {
            setFile(f);
            if (!f) setRemoveExisting(true);
            else setRemoveExisting(false);
          }}
        />
      )}

      <Label hint={t('item.iconTypeHint')}>
        {t('item.iconType')}
        <FurnitureTypePicker value={iconType} onChange={setIconType} />
      </Label>

      <Label required>
        {t('item.name')}
        <Input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (e.target.value.trim()) setNameError(false);
          }}
          placeholder={t('item.namePlaceholder')}
          aria-invalid={nameError}
          autoFocus
        />
        {nameError && <span className="text-[12px] text-clay">{t('auth.errors.required')}</span>}
      </Label>

      <Label>
        {t('item.nickname')}
        <Input
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          placeholder={name.trim() || t('item.namePlaceholder')}
        />
      </Label>

      <div className="grid grid-cols-2 gap-3">
        <Label>
          {t('item.room')}
          <Select
            ariaLabel={t('item.room')}
            value={room}
            placeholder={t('item.selectPlaceholder')}
            options={roomOpts}
            onValueChange={(v) => (v === ADD_ROOM ? setCustomMode('room') : setRoom(v))}
          />
        </Label>
        <Label>
          {t('item.category')}
          <Select
            ariaLabel={t('item.category')}
            value={category}
            placeholder={t('item.selectPlaceholder')}
            options={categoryOpts}
            onValueChange={(v) => (v === ADD_CATEGORY ? setCustomMode('category') : setCategory(v))}
          />
        </Label>
        <Label>
          {t('item.destination')}
          <Select<Destination>
            ariaLabel={t('item.destination')}
            value={destination}
            options={destinationOptions(t) as { value: Destination; label: string }[]}
            onValueChange={setDestination}
          />
        </Label>
        <Label>
          {t('item.status')}
          <Select<ProgressStatus>
            ariaLabel={t('item.status')}
            value={status}
            options={statusOptions(t) as { value: ProgressStatus; label: string }[]}
            onValueChange={setStatus}
          />
        </Label>
      </div>

      <button
        type="button"
        onClick={() => setShowMore((s) => !s)}
        className="flex items-center gap-1.5 text-petrol text-[14px] font-medium"
      >
        <ChevronDown
          className={`size-4 transition-transform ${showMore ? 'rotate-180' : ''}`}
        />
        {t('item.moreDetails')}
      </button>

      {showMore && (
        <Stack gap="sm" className="surface-tint p-4">
          <div>
            <span className="text-[13px] font-medium">{t('item.dimensions')}</span>
            <div className="grid grid-cols-4 gap-2 mt-1">
              <Input placeholder={t('item.length')} inputMode="decimal" value={length} onChange={(e) => setLength(e.target.value)} />
              <Input placeholder={t('item.width')} inputMode="decimal" value={width} onChange={(e) => setWidth(e.target.value)} />
              <Input placeholder={t('item.height')} inputMode="decimal" value={height} onChange={(e) => setHeight(e.target.value)} />
              <Select
                ariaLabel={t('item.unit')}
                value={unit}
                placeholder={t('item.unit')}
                options={unitOptions(t)}
                onValueChange={(v) => setUnit(v as DimensionUnit)}
              />
            </div>
          </div>
          <Label>
            {t('item.weight')}
            <div className="relative">
              <Input
                inputMode="decimal"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="0"
                className="pe-10"
              />
              <span className="absolute top-1/2 -translate-y-1/2 end-3 text-[13px] text-graphite-soft pointer-events-none">
                {units.weightLabel}
              </span>
            </div>
          </Label>
          <div className="grid grid-cols-2 gap-3">
            <Label>
              {t('item.condition')}
              <Select
                ariaLabel={t('item.condition')}
                value={condition}
                placeholder={t('item.selectPlaceholder')}
                options={conditionOptions(t)}
                onValueChange={(v) => setCondition(v as Condition)}
              />
            </Label>
            <Label>
              {t('item.quantity')}
              <Input inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </Label>
            <Label>
              {t('item.estimatedValue')}
              <Input inputMode="decimal" value={estimatedValue} onChange={(e) => setEstimatedValue(e.target.value)} />
            </Label>
            <Label>
              {t('item.currency')}
              <Select
                ariaLabel={t('item.currency')}
                value={currency}
                options={CURRENCIES.map((c) => ({ value: c, label: c }))}
                onValueChange={setCurrency}
              />
            </Label>
            <Label>
              {t('item.box')}
              <Select
                ariaLabel={t('item.box')}
                value={boxId || NO_BOX}
                options={boxOpts}
                onValueChange={(v) => setBoxId(v === NO_BOX ? '' : v)}
              />
            </Label>
            <Label>
              {t('item.storageLocation')}
              <Input value={storageLocation} onChange={(e) => setStorageLocation(e.target.value)} />
            </Label>
          </div>
          <label className="flex items-center gap-2 text-[14px]">
            <input type="checkbox" checked={fragile} onChange={(e) => setFragile(e.target.checked)} className="size-4 accent-[var(--color-petrol)]" />
            {t('item.fragile')}
          </label>
          <Label>
            {t('item.notes')}
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('item.notesPlaceholder')} rows={3} />
          </Label>
        </Stack>
      )}

      <Button size="lg" className="w-full" disabled={busy} onClick={submit}>
        {busy ? (busyLabel ?? t('item.saving')) : submitLabel}
      </Button>

      <Dialog
        open={customMode !== null}
        onOpenChange={(o) => !o && setCustomMode(null)}
        title={t(customMode === 'category' ? 'item.addCustomCategory' : 'item.addCustomRoom')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCustomMode(null)}>
              {t('item.cancel')}
            </Button>
            <Button onClick={confirmCustom} disabled={customBusy}>
              {t('item.save')}
            </Button>
          </div>
        }
      >
        <Input
          value={customName}
          onChange={(e) => setCustomName(e.target.value)}
          placeholder={t(
            customMode === 'category' ? 'item.customCategoryPrompt' : 'item.customRoomPrompt',
          )}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void confirmCustom();
          }}
          autoFocus
        />
      </Dialog>
    </Stack>
  );
}
